import { useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { getBaseApiUrl } from './EnvironmentManager';

// Enum for prompt profile types
export enum PromptProfile {
  FLAT_CENTER_SAFE_MARGINS = 'flat-center-safe-margins',
  FLAT_LARGE_GALLERY = 'flat-large-gallery',
  FLAT_COMPACT_CENTER = 'flat-compact-center',
  WRAP_SURFACE = 'wrap-surface',
  SEAMLESS_TILE = 'seamless-tile',
  ROUND_DIE_CUT = 'round-die-cut',
  ROUND_TINY_ICON = 'round-tiny-icon',
}

// Type for the catalog item
export type CatalogItem = {
  id: string;
  name: string;
  category?: string; // SEO-friendly category for URLs (e.g., "wall-art", "bedding")
  description: string;
  previewImageUrls: string[];
  orientation: string;
  promptModifier?: string; // Optional prompt enhancement modifier
  promptProfile?: PromptProfile; // Optional prompt profile for automatic enhancement
  variants: CatalogItemVariant[];
  accessRoles?: string[]; // Optional: Restrict access to users with specific roles
};

// Type for each variant within a catalog item
export type CatalogItemVariant = {
  id: string;
  name: string;
  price: number;
  previewImageUrls: string[];
  orientation: string;
  productionTime: string;
  vendorName: string;
  vendorData: any;
  status?: 'ACTIVE' | 'DISCONTINUED';
  promptModifier?: string; // Optional variant-specific prompt enhancement modifier
  promptProfile?: PromptProfile; // Optional variant-specific prompt profile
  productPreviewIndex?: number; // Index of the preview image from vendor data to use (default: 0)
};

// Full API response type for pagination
type CatalogApiResponse = {
  items: CatalogItem[];
  lastKey?: Record<string, any> | null;
};

const getCatalogApiUrl = () => {
  return `${getBaseApiUrl()}/catalog`;
};

const useCatalogApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  /**
   * Fetch all catalog items (with optional pagination)
   */
  const getCatalogItems = useCallback(
    async (limit: number = 10, lastKey?: Record<string, any>): Promise<CatalogApiResponse> => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        lastKey: lastKey ? JSON.stringify(lastKey) : '',
      });

      const url = `${getCatalogApiUrl()}?${params.toString()}`;

      try {
        const accessToken = await getAccessTokenSilently();
        const response = await axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (
          response?.status === 200 &&
          response?.data?.status === 'success' &&
          response?.data?.data
        ) {
          const items: CatalogItem[] = response.data.data || [];
          return { items };
        } else {
          throw new Error('No catalog data found or request failed.');
        }
      } catch (err) {
        console.error('Error fetching catalog items:', err);
        throw new Error('Failed to retrieve catalog items from the API.');
      }
    },
    [getAccessTokenSilently]
  );

  /**
   * Fetch a single catalog item by ID
   */
  const getCatalogItem = useCallback(
    async (catalogItemId: string): Promise<CatalogItem> => {
      try {
        const accessToken = await getAccessTokenSilently();
        const url = `${getCatalogApiUrl()}/${catalogItemId}`;

        const response = await axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (
          response?.status === 200 &&
          response?.data?.status === 'success' &&
          response?.data?.data
        ) {
          return response.data.data as CatalogItem;
        } else {
          throw new Error('Catalog item not found or response invalid.');
        }
      } catch (err) {
        console.error(`Error fetching catalog item ${catalogItemId}:`, err);
        throw new Error(`Failed to retrieve catalog item ${catalogItemId}`);
      }
    },
    [getAccessTokenSilently]
  );

  /**
   * Find a variant from a catalog item.
   * - If `catalogItemVariantId` is empty/undefined/null, returns the first variant.
   * - If an ID is provided but not found, throws.
   */
  const getCatalogItemVariant = useCallback(
    async (
      catalogItemId: string,
      catalogItemVariantId?: string | null
    ): Promise<CatalogItemVariant> => {
      const item = await getCatalogItem(catalogItemId);
      const variants = item?.variants ?? [];

      if (variants.length === 0) {
        throw new Error(`No variants found for catalog item ${catalogItemId}`);
      }

      const id = catalogItemVariantId?.trim();
      if (!id) {
        // Missing/empty ID: pick the first variant
        return variants[0];
      }

      const variant = variants.find((v) => v.id === id);
      if (!variant) {
        throw new Error(
          `Variant ${id} not found for catalog item ${catalogItemId}`
        );
      }

      return variant;
    },
    [getCatalogItem]
  );

  return useMemo(() => ({
    getCatalogItems,
    getCatalogItem,
    getCatalogItemVariant,
  }), [getCatalogItems, getCatalogItem, getCatalogItemVariant]);

};

export default useCatalogApi;
